import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchPublishedBundles } from '@/services/bundleService';
import { Bundle, SupportedCurrency } from '@/types/bundle';
import { useCurrency } from '@/context/CurrencyContext';
import LoadingSpinner from '@/components/LoadingSpinner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sparkles, Layers, Package, Truck } from 'lucide-react';
import ScrollDownFAB from '@/components/ScrollDownFAB';

const Bundles: React.FC = () => {
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { selectedCurrency, formatDisplayAmount } = useCurrency();

  useEffect(() => {
    const loadBundles = async () => {
      try {
        setLoading(true);
        const data = await fetchPublishedBundles();
        setBundles(data);
        setError(null);
      } catch (err) {
        console.error('Failed to load bundles:', err);
        setError('Unable to load bundles right now. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadBundles();
  }, []);

  const currencyKey = selectedCurrency.toLowerCase() as SupportedCurrency;

  const getPriceDisplay = (bundle: Bundle) => {
    const prices = bundle.prices?.[currencyKey] || {};
    const parts: string[] = [];

    if (bundle.purchaseTypes.subscription && prices.subscription != null) {
      parts.push(`Subscription ${formatDisplayAmount(prices.subscription)}/mo`);
    }

    if (bundle.purchaseTypes.oneTime && prices.oneTime != null) {
      parts.push(`One-Time ${formatDisplayAmount(prices.oneTime)}`);
    }

    return parts.length > 0 ? parts.join(' • ') : 'Pricing unavailable';
  };

  const handleViewBundle = (slug: string) => {
    navigate(`/bundle/${slug}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 pb-10">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className="mb-12 text-center">
          <div className="inline-flex items-center gap-3 mb-6 sm:mb-4">
            <div className="relative">
              <div className="w-10 h-10 sm:w-14 sm:h-14 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl flex items-center justify-center shadow-xl transform rotate-6 sm:hover:-rotate-6 transition-transform duration-500">
                <Layers className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
              </div>
              <div className="absolute -top-2 -right-2 w-5 h-5 sm:w-6 sm:h-6 bg-jamaica-yellow rounded-full flex items-center justify-center animate-bounce">
                <span className="text-[10px] sm:text-xs font-bold text-white">✦</span>
              </div>
            </div>
            <div className="bg-gradient-to-r from-jamaica-green/10 to-jamaica-yellow/10 px-3 py-1.5 sm:px-5 sm:py-2 rounded-2xl border border-jamaica-green/20 shadow-lg">
              <span className="text-xs sm:text-sm font-bold text-jamaica-black">
                Curated Experiences
              </span>
            </div>
          </div>

          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-2 sm:mb-4 leading-tight">
            <span className="bg-gradient-to-r from-jamaica-green via-jamaica-yellow to-jamaica-green bg-clip-text text-transparent p-1">
              Discover Our JamPack'd Bundles
            </span>
          </h1>
          <p className="hidden sm:block text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Subscribe or shop one-time for island-inspired collections packed with <span className="text-jamaica-yellow font-semibold">beloved flavours</span> and <span className="text-jamaica-green font-semibold">authentic treasures</span> curated by our JamPack'd team.
          </p>
          <p className="sm:hidden text-sm text-gray-600 max-w-md mx-auto leading-relaxed">
            Explore curated JamPack'd boxes full of authentic island favourites.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <LoadingSpinner size="lg" />
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center">
            <p className="text-red-700 font-medium">{error}</p>
          </div>
        ) : bundles.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-12 text-center">
            <Layers className="h-10 w-10 mx-auto text-gray-300 mb-4" />
            <h2 className="text-lg font-semibold text-gray-800">No bundles available right now</h2>
            <p className="text-sm text-gray-500 mt-2">
              We’re cooking up something special. Check back soon for new JamPack'd experiences!
            </p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 scroll-target">
            {bundles.map((bundle) => {
              const previewItems = (bundle.items ?? []).map((item) => {
                const quantity = item.quantity ?? 1;
                const name = item.productName || 'Item';
                const hasWeightInfo = Boolean(item?.netWeight || item?.weightUnit);
                const weightDisplay = hasWeightInfo
                  ? `${item?.netWeight ?? '?'}${item?.weightUnit ? item.weightUnit.toLowerCase() : ''}`
                  : '';
                return `${quantity}× ${name}${hasWeightInfo ? ` (${weightDisplay})` : ''}`;
              });
              const previewText = previewItems.length > 0
                ? (() => {
                    const visible = previewItems.slice(0, 4);
                    const ellipsis = previewItems.length > 4 ? '…' : '';
                    return `${visible.join(', ')}${ellipsis}`;
                  })()
                : 'Curated JamPack\'d favourites';

              return (
                <div
                  key={bundle.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleViewBundle(bundle.slug)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleViewBundle(bundle.slug);
                    }
                  }}
                  className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-transform transition-shadow hover:shadow-lg hover:-translate-y-0.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-jamaica-green/40"
                >
                <div className="relative w-full overflow-hidden rounded-t-2xl bg-gray-100 aspect-[16/9]">
                  <img
                    src={bundle.heroImage || bundle.images?.[0] || '/api/placeholder/720/405'}
                    alt={bundle.name}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                  />
                </div>

                <div className="flex h-full flex-col p-6">
                  <div className="flex items-center gap-2 mb-3">
                    {bundle.purchaseTypes.oneTime && (
                      <Badge variant="outline" className="border-jamaica-green text-jamaica-green flex items-center gap-1">
                        <Package className="h-3 w-3" /> One-Time
                      </Badge>
                    )}
                    {bundle.purchaseTypes.subscription && (
                      <Badge variant="outline" className="border-jamaica-yellow text-jamaica-yellow flex items-center gap-1">
                        <Sparkles className="h-3 w-3" /> Subscription
                      </Badge>
                    )}
                  </div>

                  <h2 className="text-xl font-semibold text-gray-900 transition-colors duration-200 group-hover:text-jamaica-green">
                    {bundle.name}
                  </h2>
                  <p className="mt-2 text-xs text-gray-500 min-h-[1.5rem]">
                    {previewText}
                  </p>
                  <p className="mt-2 text-sm text-gray-600 line-clamp-2">{bundle.description}</p>

                  <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-2">
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-semibold text-gray-900">{getPriceDisplay(bundle)}</span>
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 bg-emerald-50 border border-emerald-100 px-2 py-1 rounded-full w-fit">
                        <Truck className="h-3 w-3" /> Shipping Included
                      </span>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      className="inline-flex items-center justify-center gap-1.5 bg-gradient-to-r from-jamaica-green to-jamaica-yellow text-white border-none shadow-sm hover:shadow-md hover:from-jamaica-green/90 hover:to-jamaica-yellow/90 focus-visible:ring-jamaica-green/60 w-full sm:w-auto"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleViewBundle(bundle.slug);
                      }}
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      <span className="text-xs font-semibold uppercase tracking-wide">Explore</span>
                    </Button>
                  </div>
                </div>
              </div>
            );
            })}
          </div>
        )}
      </div>
      {bundles.length > 0 && (
        <ScrollDownFAB
          scrollTarget=".scroll-target"
          tooltipText="Scroll to see more bundles"
          className="!bottom-24 sm:!bottom-12"
        />
      )}
    </div>
  );
};

export default Bundles;
